import type { DocumentReconstructedDocumentsListResponse } from '@avisoma/shared';
import { isHugeLineageVisual } from '../../lib/classificationVisuals';

export const RECONSTRUCTED_DOCUMENTS_PAGE_SIZE = 200;

export type ReconstructionPaginationAction = 'drain' | 'idle';

type ReconstructionGeometryDeferralInput = {
  isPageWorkloadKnown: boolean;
  isHugeClassificationVisual: boolean;
  showLineage: boolean;
  isHugeLineageVisual: boolean;
};

type ReconstructionVisualStateInput = {
  isPageWorkloadKnown: boolean;
  isPageWorkloadFetching: boolean;
  pageWorkloadError: Error | undefined;
  isGeometryLoading: boolean;
  geometryError: Error | undefined;
};

type ReconstructionVisualLoadingInput = {
  isQueryEnabled: boolean;
  shouldDrainForVisuals: boolean;
  isQueryPending: boolean;
  isFetchingNextPage: boolean;
  hasMore: boolean;
  hasError: boolean;
};

type ReconstructionPaginationStateInput = {
  pages: DocumentReconstructedDocumentsListResponse[];
  fallbackTotalCount: number;
  isPageWorkloadKnown: boolean;
  isHugeClassificationVisual: boolean;
  hasSegmentedLineage: boolean;
  pageCount: number;
  visualsRequested: boolean;
  isFetchingNextPage: boolean;
  hasError: boolean;
};

type ReconstructionVisualRetryInput = {
  requestVisuals: () => void;
  pageWorkloadError: Error | undefined;
  refetchPages: () => unknown;
  restartGeometry: () => void;
};

type ReconstructionPreviewInstanceKeyInput = {
  documentId: string;
  reconstructionStatus: string | undefined;
  reconstructionCompletedAt: string | undefined;
  recoveryVersion: number;
};

/** Keep the preview's local card window scoped to a document reconstruction query generation. */
export function reconstructedDocumentsPreviewInstanceKey({
  documentId,
  reconstructionStatus,
  reconstructionCompletedAt,
  recoveryVersion,
}: ReconstructionPreviewInstanceKeyInput): string {
  return `${documentId}:${reconstructionStatus ?? 'unavailable'}:${reconstructionCompletedAt ?? 'unavailable'}:${recoveryVersion}`;
}

/**
 * A cursor page can be retried by TanStack Query, so preserve cursor order while ensuring a
 * repeated item never creates a duplicate preview card or reconstruction geometry band.
 */
export function flattenReconstructedDocumentPages(pages: DocumentReconstructedDocumentsListResponse[]) {
  const uniqueItems = new Map<string, DocumentReconstructedDocumentsListResponse['items'][number]>();
  for (const page of pages) {
    for (const item of page.items) {
      if (!uniqueItems.has(item.reconstructedDocumentId)) {
        uniqueItems.set(item.reconstructedDocumentId, item);
      }
    }
  }
  return [...uniqueItems.values()];
}

/** Defer geometry until page workload is known, then only while every visible consumer remains gated. */
export function shouldDeferReconstructionGeometry({
  isPageWorkloadKnown,
  isHugeClassificationVisual,
  showLineage,
  isHugeLineageVisual,
}: ReconstructionGeometryDeferralInput): boolean {
  return !isPageWorkloadKnown || (isHugeClassificationVisual && (!showLineage || isHugeLineageVisual));
}

/** Geometry consumers wait for page and reconstruction data, but surface either query's error. */
export function getReconstructionVisualState({
  isPageWorkloadKnown,
  isPageWorkloadFetching,
  pageWorkloadError,
  isGeometryLoading,
  geometryError,
}: ReconstructionVisualStateInput) {
  return {
    loading:
      (!isPageWorkloadKnown && (isPageWorkloadFetching || !pageWorkloadError)) || isGeometryLoading,
    error: pageWorkloadError ?? geometryError,
  };
}

/** Initial cursor loading has an unknown count, so a zero fallback cannot gate geometry loading. */
export function getIsReconstructionVisualLoading({
  isQueryEnabled,
  shouldDrainForVisuals,
  isQueryPending,
  isFetchingNextPage,
  hasMore,
  hasError,
}: ReconstructionVisualLoadingInput): boolean {
  return (
    isQueryEnabled &&
    shouldDrainForVisuals &&
    !hasError &&
    (isQueryPending || isFetchingNextPage || hasMore)
  );
}

/**
 * Converts the shared cursor-query result into one stable view model. Preview pagination and
 * visual draining both consume this so they cannot derive contradictory item windows.
 */
export function getReconstructedDocumentsPaginationState({
  pages,
  fallbackTotalCount,
  isPageWorkloadKnown,
  isHugeClassificationVisual,
  hasSegmentedLineage,
  pageCount,
  visualsRequested,
  isFetchingNextPage,
  hasError,
}: ReconstructionPaginationStateInput) {
  const items = flattenReconstructedDocumentPages(pages);
  const latestPage = pages[pages.length - 1];
  const totalCount = latestPage?.totalCount ?? fallbackTotalCount;
  const hasMore = latestPage?.hasMore ?? false;
  const showLineage = hasSegmentedLineage || totalCount >= 2;
  const isHugeLineage = isHugeLineageVisual(pageCount, totalCount);
  const deferReconstructionGeometry = shouldDeferReconstructionGeometry({
    isPageWorkloadKnown,
    isHugeClassificationVisual,
    showLineage,
    isHugeLineageVisual: isHugeLineage,
  });
  const shouldDrainForVisuals = !deferReconstructionGeometry || visualsRequested;
  const nextAction: ReconstructionPaginationAction =
    hasMore && shouldDrainForVisuals && !isFetchingNextPage && !hasError ? 'drain' : 'idle';

  return {
    items,
    totalCount,
    hasMore,
    showLineage,
    isHugeLineageVisual: isHugeLineage,
    deferReconstructionGeometry,
    remaining: Math.max(0, totalCount - items.length),
    previewCanLoadMore: hasMore && !isFetchingNextPage,
    shouldDrainForVisuals,
    nextAction,
  };
}

/** Retry a visual after opting into the cursor drain that supplies its geometry. */
export function runReconstructionVisualRetry({
  requestVisuals,
  pageWorkloadError,
  refetchPages,
  restartGeometry,
}: ReconstructionVisualRetryInput): void {
  requestVisuals();

  if (pageWorkloadError) {
    void refetchPages();
    return;
  }

  restartGeometry();
}

/** The backend deliberately uses 409 when a cursor belongs to an older reconstruction run. */
export function isReconstructionGenerationConflict(error: unknown) {
  return (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof error.response === 'object' &&
    error.response !== null &&
    'status' in error.response &&
    error.response.status === 409
  );
}
